import React, { useState, useRef } from "react";
import { LuCircleCheck } from "react-icons/lu";
import { twMerge } from "tailwind-merge";
import { FolderOpen } from "lucide-react";
import FileItem from "./FileItem";

// Types
type IFileItem = {
  id: string;
  file: File;
  status: "pending" | "uploading" | "success" | "error";
  progress?: number;
  errorMessage?: string;
};

type IDocumentUpload = {
  title: string;
  name: string;
  onChange: (files: { id: string; file: File }[]) => void;
  onDelete?: (fileId: string) => void;
  acceptedFileTypes?: string[];
  maxFileSize?: number; // in MB
  maxFiles?: number;
  required?: boolean;
  label?: string;
  className?: string;
  disabled?: boolean;
};

const DocumentUpload: React.FC<IDocumentUpload> = ({
  title,
  name,
  onChange,
  onDelete,
  acceptedFileTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "text/plain",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ],
  maxFileSize = 10, // 10MB default for documents
  maxFiles = 5,
  required = false,
  label,
  className,
  disabled = false,
}) => {
  const [files, setFiles] = useState<IFileItem[]>([]);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState<string>("");
  const [deletingFileId, setDeletingFileId] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = (file: File): string | null => {
    if (!acceptedFileTypes.includes(file.type)) {
      const extensions = acceptedFileTypes
        .map((type) => type.split("/")[1])
        .join(", ");
      return `Invalid file type. Accepted: ${extensions.toUpperCase()}`;
    }
    if (file.size > maxFileSize * 1024 * 1024) {
      return `File too large. Max size: ${maxFileSize}MB`;
    }
    if (files.length >= maxFiles) {
      return `Maximum ${maxFiles} files allowed`;
    }
    return null;
  };

  const processFiles = async (newFiles: FileList | File[]) => {
    const fileArray = Array.from(newFiles);
    let hasError = false;
    const validFiles: IFileItem[] = [];

    for (const file of fileArray) {
      const validationError = validateFile(file);
      if (validationError) {
        setError(validationError);
        hasError = true;
        setTimeout(() => setError(""), 3000);
        continue;
      }

      validFiles.push({
        id: `${Date.now()}-${Math.random()}`,
        file,
        status: "pending",
      });
    }

    if (validFiles.length > 0) {
      const updatedFiles = [...files, ...validFiles];
      setFiles(updatedFiles);
      onChange(updatedFiles.map((f) => ({ id: f.id, file: f.file })));

      // Simulate upload progress
      validFiles.forEach((fileItem) => {
        simulateUpload(fileItem.id);
      });
    }

    if (!hasError && fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const simulateUpload = (fileId: string) => {
    setFiles((prev) =>
      prev.map((f) =>
        f.id === fileId ? { ...f, status: "uploading", progress: 0 } : f,
      ),
    );

    let progress = 0;
    const interval = setInterval(() => {
      progress += 10;
      setFiles((prev) =>
        prev.map((f) => (f.id === fileId ? { ...f, progress } : f)),
      );

      if (progress >= 100) {
        clearInterval(interval);
        setFiles((prev) =>
          prev.map((f) => (f.id === fileId ? { ...f, status: "success" } : f)),
        );
      }
    }, 200);
  };

  const removeFile = async (fileId: string) => {
    setDeletingFileId(fileId);

    if (onDelete) {
      await onDelete(fileId);
    }

    const updatedFiles = files.filter((f) => f.id !== fileId);
    setFiles(updatedFiles);

    setDeletingFileId(null);
  };

  const handleDrag = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);

    if (disabled) return;

    const droppedFiles = e.dataTransfer.files;
    if (droppedFiles?.length) {
      processFiles(droppedFiles);
    }
  };

  return (
    <div className="w-full">
      {/* Upload Area */}
      <div
        onClick={() => !disabled && fileInputRef.current?.click()}
        onDragEnter={handleDrag}
        onDragLeave={handleDrag}
        onDragOver={handleDrag}
        onDrop={handleDrop}
        className={twMerge(
          "relative border-2 border-dashed rounded-xl cursor-pointer transition-all duration-300",
          dragActive
            ? "border-blue-500 bg-blue-50"
            : "border-gray-300 hover:border-primary-400 hover:bg-primary-50/20",
          disabled && "opacity-50 cursor-not-allowed",
          className,
        )}
      >
        <div className="p-8 flex flex-col items-center justify-center text-center">
          <FolderOpen
            size={48}
            className={dragActive ? "text-blue-500" : "text-gray-400"}
          />

          <div className="mt-2">
            <p className="text-lg font-medium text-gray-700">
              {title}
              {required && <span className="text-red-500 ml-1">*</span>}
            </p>
            <p className="text-sm text-gray-500 mt-1">
              {label || `Drag & drop documents or click to browse`}
            </p>
            <div className="grid text-xs text-gray-400 mt-2 space-x-2">
              <span>
                <span>Max {maxFiles} files</span>
                <span> • </span>
                <span>Up to {maxFileSize}MB each</span>
              </span>

              <span>PDF, DOC, DOCX, TXT, XLS, PPT</span>
            </div>
          </div>
        </div>

        {error && (
          <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2 bg-red-500 text-white px-3 py-1 rounded-md text-sm whitespace-nowrap z-10">
            {error}
          </div>
        )}

        <input
          ref={fileInputRef}
          type="file"
          name={name}
          multiple
          accept={acceptedFileTypes.join(",")}
          className="hidden"
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            if (e.currentTarget.files?.length) {
              processFiles(e.currentTarget.files);
            }
          }}
          disabled={disabled}
        />
      </div>

      {/* File List */}
      {files.length > 0 && (
        <div className="mt-6 space-y-3">
          <div className="flex justify-between items-center">
            <h4 className="text-sm font-medium text-gray-700">
              Uploaded Documents ({files.length}/{maxFiles})
            </h4>
            {files.filter((f) => f.status === "success").length ===
              files.length &&
              files.length > 0 && (
                <div className="flex items-center gap-1 text-green-600 text-sm">
                  <LuCircleCheck size={16} />
                  <span>All documents uploaded</span>
                </div>
              )}
          </div>

          <div className="space-y-2 max-h-96 overflow-y-auto custom-scrollbar">
            {files.map((fileItem) => (
              <FileItem
                key={fileItem.id}
                fileItem={fileItem}
                onRemove={removeFile}
                deleteLoading={deletingFileId === fileItem.id}
              />
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
